import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { X, Download } from "lucide-react";

interface LightboxProps {
  isOpen: boolean;
  onClose: () => void;
  src: string; // volledige URL naar de afbeelding
  fileName?: string;
}

/** Lightbox voor een vergrote weergave van een document-thumbnail (alleen
 *  afbeeldingen — PDF's worden door de caller in een nieuw tabblad geopend).
 *  Bevat een downloadknop; de afbeelding wordt direct vanaf de statische
 *  /uploads-route geladen (geen auth nodig). */
export function Lightbox({ isOpen, onClose, src, fileName }: LightboxProps) {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative max-w-4xl w-full transform transition-all">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-white/80 font-medium truncate">
                    {fileName || "Document"}
                  </span>
                  <div className="flex items-center gap-2">
                    <a
                      href={src}
                      download={fileName || "document.jpg"}
                      className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                      title="Downloaden"
                    >
                      <Download size={22} />
                    </a>
                    <button
                      onClick={onClose}
                      className="p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors"
                      title="Sluiten"
                    >
                      <X size={22} />
                    </button>
                  </div>
                </div>
                <div className="rounded-2xl overflow-hidden bg-black/40 flex items-center justify-center max-h-[80vh]">
                  <img
                    src={src}
                    alt={fileName || "Document"}
                    className="max-h-[80vh] w-auto object-contain"
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}